import React, { useEffect } from "react";
import { Container, Col, Row } from "reactstrap";
import { Link } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { Fields } from "@repoflow/components/fields";
import { useSectionContext } from "@repoflow/components/section";
import * as SiteFileQL from "queries/site-file";
import * as widgets from "widgets/register";
import { isPreviewExt } from "@common/schema/site-file/kind";
import { isPublic } from "@common/schema/site-file/visibility";
import {
  siteFileContentUrl,
  siteFilePublicContentUrl,
  contentFileName,
} from "@common/schema/site-file/url";

// Hoisted, for the reason recorded on the section's view screen: a config
// literal is a new object every render and the provider rebuilds underneath it.
//
// `hash` and `bucketId` are not here. Both are on the wire because the app needs
// them — the hash to create the row, the bucketId to address the bytes — and
// neither says anything to the person looking at the file.
const VIEW_FIELDS = {
  "/Public/SiteFile": {
    fields: ["name", "ext", "size", "status", "visibility", "description", "tags", "createdAt", "updatedAt"],
  },
};

export const Sidebar = () => null;

export const Indice = () => null;

// WHAT THIS SCREEN CAN OFFER FOR A ROW.
//
// A value rather than three conditions spread through the JSX, and exported so
// it can be pinned without standing up a provider — the same reason
// ../../new/index.js exports `baseNameOf`.
//
// `size` is the gate, NOT `status`, and the distinction is the useful part.
// bucketws' upload-handler writes the object and the real size in one step and
// only then leaves an image at "processing" while it generates minimaps, so a
// row can be perfectly readable and still say processing; gating on the status
// would hide a good file for as long as the derived sizes take. A row that has
// no bytes yet carries `size: 0`, which is what the create wrote.
//
// The no-bytes case renders nothing at all rather than explaining itself. A
// line of prose on a screen is a test-breaking change — it repeats the words
// the specs match on and turns a `getByText` into a strict-mode violation — and
// the absence of the two buttons already says the file is not there yet.
export const readBackOf = (values) => {
  const { siteId, kind, fileId, name, ext, size } = values;

  if (!(Number(size) > 0) || !fileId || !ext) {
    return { href: null, filename: null, preview: false, publicHref: null };
  }

  return {
    href: siteFileContentUrl({ siteId, kind, fileId, name, ext }),
    filename: contentFileName(name, ext),
    preview: isPreviewExt(ext),
    // The OTHER url — the one a visitor uses, which this screen shows and never
    // follows. It is relative and carries no hostname on purpose: the site's
    // own edge inserts whichever verified domain the visitor arrived on, so one
    // string keeps working when a domain is attached, detached or swapped. See
    // @common/schema/site-file/url.
    //
    // Offered only for a file that is public, because for any other row it
    // would be a URL that answers nothing — and a broken address on a screen is
    // worse than no address at all.
    publicHref: isPublic(values)
      ? siteFilePublicContentUrl({ kind, fileId, name, ext })
      : null,
  };
};

export const Content = () => {
  const { values, schema, base } = useSectionContext();
  const { siteId, kind, fileId, status } = values;

  const readBack = readBackOf(values);

  const [sync] = useMutation(SiteFileQL.sync, { update: SiteFileQL.update() });

  // THIS IS WHERE A FILE FINDS OUT IT FINISHED UPLOADING.
  //
  // bucketws fills in the real size, status and derived metadata from its own
  // S3 event handler, after this service's create mutation has already
  // answered — so nothing here is told when it lands. Opening the file is the
  // moment we go and ask.
  //
  // Fired only while the row still reads "processing", so an ordinary look at a
  // settled file costs nothing. The mutation is a no-op write when nothing
  // changed (see model/site-file.sync), which is what keeps `updatedAt` tracking
  // being CHANGED rather than being looked at.
  useEffect(() => {
    if (status !== "processing") return;
    sync({ variables: { siteId, kind, fileId } }).catch(() => {
      // Swallowed: the row still renders, it just renders the state we already
      // had. An error toast for a background refresh would be noise on a screen
      // that is working.
    });
  }, [status, siteId, kind, fileId, sync]);

  return (
    <Container className="mt-4">
      <Row>
        <Col>
          <Fields
            schema={schema}
            widgets={widgets}
            target={{ $ref: "/Public/SiteFile" }}
            config={VIEW_FIELDS}
            values={values}
          />
        </Col>
      </Row>

      {/* THE BYTES COME BACK THROUGH THIS ORIGIN, NOT BUCKETWS'.
          ======================================================

          `/file/*` on this distribution is an authenticated proxy. Its
          origin-request edge function identifies the viewer from their pagews
          session, confirms the row belongs to that viewer's organization, and
          only THEN attaches pagews' bucketws service key and re-points the
          origin at bucketws' distribution. So a plain relative href is all this
          screen needs, and the credential — which owns every bucket this
          service holds, for every one of its customers — never comes near a
          browser.

          This is what P159 was. It was open because bucketws serves bytes from
          an authenticated path and the person on this screen has no bucketws
          session at all; the shape of the fix, the alternative that was
          rejected, and the evidence that the CloudFront-to-CloudFront hop works
          are in docs/pagews-files.md §7. */}
      {readBack.preview ? (
        <Row>
          <Col>
            <img
              className="img-fluid border rounded mt-3"
              style={{ maxHeight: "24rem" }}
              src={readBack.href}
              alt={readBack.filename}
            />
          </Col>
        </Row>
      ) : null}

      {/* WHAT TO PASTE INTO A PAGE.
          =========================

          A page element carries raw html (`code` and `section` both do), so
          this is the whole of "put this image on the site" today: copy the
          path, paste it into a `src`. It resolves anonymously on any hostname
          the site has attached and verified, through the same `/file/*`
          behaviour the preview above uses and a different branch of the same
          edge function — docs/pagews-files.md §7.

          Not a link. It does not resolve on the app's own hostname, which is
          the one this screen is being read on: the by-host route is the
          tenant's edge inserting a domain, and the app's origin deliberately
          serves no tenant content (P142). */}
      {readBack.publicHref ? (
        <Row>
          <Col>
            <small className="text-muted d-block mt-3">Public path</small>
            <code>{readBack.publicHref}</code>
          </Col>
        </Row>
      ) : null}

      <Row>
        <Col>
          {readBack.href ? (
            <>
              {/* An anchor rather than a Link, because this leaves the SPA. The
                  `download` attribute is honoured because the URL is
                  same-origin, so the file lands under the name the person gave
                  it rather than under the uuid bucketws minted. */}
              <a
                className={"btn btn-secondary mt-2"}
                href={readBack.href}
                download={readBack.filename}
              >
                Download
              </a>{" "}
            </>
          ) : null}
          <Link className={"btn btn-primary mt-2"} to={`${base}/edit`}>
            Edit
          </Link>{" "}
          <Link className={"btn btn-danger mt-2"} to={`${base}/remove`}>
            Remove
          </Link>
        </Col>
      </Row>
    </Container>
  );
};
